import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { checkAuthAction, loginAction, registerAction } from '../actions'
import { AuthStatus, type User } from '../interfaces'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const authStatus = ref(AuthStatus.Checking)
  const user = ref<User | undefined>()
  const token = ref(useLocalStorage('token', ''))

  const login = async (email: string, password: string) => {
    try {
      const logingResp = await loginAction(email, password)
      if (!logingResp.ok) {
        return logout()
      }

      user.value = logingResp.user
      token.value = logingResp.token
      authStatus.value = AuthStatus.Authenticated

      return true
    } catch (error) {
      return logout()
    }
  }

  const register = async (fullname: string, email: string, password: string) => {
    try {
      const registerResp = await registerAction(fullname, email, password)
      if (!registerResp.ok) {
        logout()
        return {
          ok: false,
          message: registerResp.message,
        }
      }

      user.value = registerResp.user
      token.value = registerResp.token
      authStatus.value = AuthStatus.Authenticated

      return { ok: true, msg: 'Usuario registrado correctamente' }
    } catch (error) {
      logout()
      return { ok: false, msg: 'error al registrar el usuario' }
    }
  }

  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      const checkResp = await checkAuthAction()

      if (!checkResp.ok) {
        localStorage.removeItem(`token`)
        authStatus.value = AuthStatus.Unauthenticated
        user.value = undefined
        token.value = ''
        return false
      }

      user.value = checkResp.user
      token.value = checkResp.token
      authStatus.value = AuthStatus.Authenticated

      return true
    } catch (error) {
      logout()
      return false
    }
  }

  const logout = () => {
    localStorage.removeItem(`token`)
    localStorage.removeItem(`email`)
    authStatus.value = AuthStatus.Unauthenticated
    user.value = undefined
    token.value = ''
    router.push({ name: 'login' })
    return false
  }

  return {
    user,
    authStatus,
    token,

    // Getters
    isChecking: computed(() => authStatus.value === AuthStatus.Checking),
    isAuthenticated: computed(() => authStatus.value === AuthStatus.Authenticated),
    isAdmin: computed(() => user.value?.roles.includes('admin') ?? false),
    username: computed(() => user.value?.fullName),

    // Actions
    login,
    register,
    checkAuthStatus,
    logout,
  }
})
